import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { MdChevronLeft, MdChevronRight, MdClose, MdEmojiEvents, MdInfoOutline, MdWorkspacePremium } from "react-icons/md";
import Modal from "../Model";
import { ANNOUNCE_EOTM, GET_EOTM_HISTORY, GET_EOTM_MONTH } from "./graphql";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

// YYYY-MM for "this month" in India time, and month arithmetic on keys.
const istNow = () => new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
const keyOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const shift = (key, by) => {
  const [y, m] = key.split("-").map(Number);
  return keyOf(new Date(y, m - 1 + by, 1));
};
const monthLabel = (key) => {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
};

const PARTS = [
  { key: "value", label: "Booking value", max: 40, cls: "bg-violet-500" },
  { key: "rating", label: "Rating", max: 25, cls: "bg-amber-400" },
  { key: "punctuality", label: "On-time starts", max: 20, cls: "bg-sky-500" },
  { key: "steps", label: "Steps followed", max: 15, cls: "bg-emerald-500" },
];

// Stacked bar of the four score parts (out of 100), minus deductions.
const ScoreBar = ({ points }) => (
  <div className="flex h-2 flex-1 min-w-[120px] rounded-full bg-gray-100 overflow-hidden" title={PARTS.map((p) => `${p.label} ${points[p.key]}/${p.max}`).join(" · ")}>
    {PARTS.map((p) => (
      <div key={p.key} className={p.cls} style={{ width: `${Math.max(0, points[p.key])}%` }} />
    ))}
  </div>
);

const AnnounceForm = ({ month, candidate, onClose, onDone }) => {
  const [prize, setPrize] = useState("1000");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [announce, { loading }] = useMutation(ANNOUNCE_EOTM);
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await announce({
        variables: { data: { month, employeeId: candidate.employeeId, prizeCoins: parseInt(prize || "0", 10), note: note || null } },
      });
      const out = res.data?.announceEotm;
      if (out?.status !== "SUCCESS") {
        setError(out?.message || "Could not announce");
        return;
      }
      onDone();
    } catch (err) {
      setError("Could not announce. Please try again.");
    }
  };
  return (
    <Modal onClose={onClose} disableOutsideClick widthClass="sm:w-[480px]">
      <form onSubmit={submit} className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Announce Employee of the Month</h2>
            <p className="text-sm text-gray-500">{monthLabel(month)} · can't be changed afterwards</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-gray-400 hover:bg-gray-100" aria-label="Close">
            <MdClose className="text-xl" />
          </button>
        </div>
        <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 flex items-center gap-3">
          <span className="h-11 w-11 rounded-full bg-gradient-to-br from-yellow-300 to-amber-500 text-white text-2xl flex items-center justify-center">
            <MdEmojiEvents />
          </span>
          <div>
            <div className="font-semibold text-gray-900">{candidate.name}</div>
            <div className="text-xs text-gray-600">
              #{candidate.rank} · score {candidate.score}/100 · {candidate.bookings} bookings · {inr(candidate.revenue)}
            </div>
          </div>
        </div>
        <label className="block">
          <span className="text-[13px] font-medium text-gray-700">Prize (coins = ₹)</span>
          <input type="number" min="0" className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={prize} onChange={(e) => setPrize(e.target.value)} />
          <span className="block text-xs text-gray-500 mt-1">Added to their coins; they can redeem it into salary. 0 for no prize.</span>
        </label>
        <label className="block">
          <span className="text-[13px] font-medium text-gray-700">Message (optional)</span>
          <input className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={note} onChange={(e) => setNote(e.target.value)} maxLength={240} placeholder="e.g. Most on-time starts and 4.9 rating" />
        </label>
        <p className="text-xs text-gray-500">They get the prize, an Employee of the Month badge and a celebration in the app.</p>
        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={loading} className="px-5 py-2 rounded-lg bg-[#FBB033] text-black font-semibold hover:brightness-95 disabled:opacity-60">
            {loading ? "Announcing…" : `Announce ${candidate.name.split(" ")[0]}`}
          </button>
        </div>
      </form>
    </Modal>
  );
};

/**
 * Employee of the Month: the month's ranking with the score breakdown
 * (computed by the backend), and announcing a winner once the month ends.
 */
const EotmTab = () => {
  const current = keyOf(istNow());
  const [month, setMonth] = useState(shift(current, -1));
  const [announcing, setAnnouncing] = useState(null);
  const q = useQuery(GET_EOTM_MONTH, { variables: { month }, fetchPolicy: "network-only" });
  const history = useQuery(GET_EOTM_HISTORY, { fetchPolicy: "network-only" });
  const data = q.data?.getEotmMonth;
  const winners = history.data?.getEotmHistory || [];
  const canGoForward = month < current;
  const eligible = useMemo(() => (data?.candidates || []).filter((c) => c.eligible), [data]);
  const notEligible = useMemo(() => (data?.candidates || []).filter((c) => !c.eligible), [data]);

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-xl border border-violet-300/30 bg-violet-500/10 px-4 py-3 text-sm text-violet-100">
        <MdInfoOutline className="text-lg mt-0.5 shrink-0" />
        <div>
          Score out of 100: <span className="text-white font-semibold">booking value 40</span> (vs. the month's best),{" "}
          <span className="text-white font-semibold">rating 25</span>, <span className="text-white font-semibold">on-time starts 20</span>{" "}
          (within 10 min of the slot), <span className="text-white font-semibold">steps followed 15</span> (Reached, Start, Scan) —
          minus 2 per step deduction. Needs at least {data?.minBookings ?? 10} completed bookings. You confirm the winner.
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <button onClick={() => setMonth(shift(month, -1))} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600" aria-label="Previous month">
              <MdChevronLeft className="text-xl" />
            </button>
            <div className="font-semibold text-gray-900 w-40 text-center">{monthLabel(month)}</div>
            <button
              onClick={() => canGoForward && setMonth(shift(month, 1))}
              disabled={!canGoForward}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 disabled:opacity-30"
              aria-label="Next month"
            >
              <MdChevronRight className="text-xl" />
            </button>
            {month === current && <span className="text-xs rounded-full bg-sky-50 text-sky-700 px-2 py-0.5">Live · month not over</span>}
          </div>
          <div className="flex flex-wrap gap-3 text-[11px] text-gray-500">
            {PARTS.map((p) => (
              <span key={p.key} className="inline-flex items-center gap-1">
                <span className={`h-2 w-2 rounded-full ${p.cls}`} /> {p.label} ({p.max})
              </span>
            ))}
          </div>
        </div>

        {data?.winner && (
          <div className="m-4 rounded-xl bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 p-4 flex items-center gap-4">
            <span className="h-14 w-14 rounded-full bg-gradient-to-br from-yellow-300 to-amber-500 text-white text-3xl flex items-center justify-center shadow">
              <MdWorkspacePremium />
            </span>
            <div className="min-w-0">
              <div className="text-xs font-semibold tracking-wide text-amber-700">EMPLOYEE OF THE MONTH</div>
              <div className="text-lg font-bold text-gray-900">{data.winner.name}</div>
              <div className="text-sm text-gray-600">
                Score {data.winner.score}/100 · prize {inr(data.winner.prizeCoins)}
                {data.winner.note ? ` · "${data.winner.note}"` : ""}
              </div>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2.5 font-medium">#</th>
                <th className="px-4 py-2.5 font-medium">Professional</th>
                <th className="px-4 py-2.5 font-medium min-w-[200px]">Score</th>
                <th className="px-4 py-2.5 font-medium text-right">Bookings</th>
                <th className="px-4 py-2.5 font-medium text-right">Value</th>
                <th className="px-4 py-2.5 font-medium text-right">Rating</th>
                <th className="px-4 py-2.5 font-medium text-right">On time</th>
                <th className="px-4 py-2.5 font-medium text-right">Steps</th>
                <th className="px-4 py-2.5 font-medium text-right">Deductions</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {q.loading && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-gray-500">
                    Loading…
                  </td>
                </tr>
              )}
              {!q.loading && !eligible.length && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-gray-500">
                    No one has {data?.minBookings ?? 10}+ completed bookings in this month yet.
                  </td>
                </tr>
              )}
              {eligible.map((c) => {
                const isWinner = data?.winner?.employeeId === c.employeeId;
                return (
                  <tr key={c.employeeId} className={`border-t border-gray-100 ${isWinner ? "bg-amber-50/60" : c.rank <= 3 ? "bg-violet-50/30" : ""}`}>
                    <td className="px-4 py-3 font-bold text-gray-500">{c.rank}</td>
                    <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                      {c.name}
                      {isWinner && <MdEmojiEvents className="inline ml-1 text-amber-500" />}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900 w-10 tabular-nums">{c.score}</span>
                        <ScoreBar points={c.points} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{c.bookings}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{inr(c.revenue)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {c.avgRating ?? "—"}
                      <span className="text-xs text-gray-400"> ({c.rated})</span>
                    </td>
                    <td className={`px-4 py-3 text-right tabular-nums ${c.onTimePct < 50 ? "text-red-600" : ""}`}>{c.onTimePct}%</td>
                    <td className="px-4 py-3 text-right tabular-nums">{c.stepsPct}%</td>
                    <td className={`px-4 py-3 text-right tabular-nums ${c.deductions ? "text-red-600" : "text-gray-400"}`}>{c.deductions}</td>
                    <td className="px-4 py-3 text-right">
                      {data?.isClosed && !data?.winner && (
                        <button
                          onClick={() => setAnnouncing(c)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap ${
                            c.rank === 1 ? "bg-[#FBB033] text-black hover:brightness-95" : "border border-gray-200 text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          Announce
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {notEligible.length > 0 && (
          <p className="px-4 py-3 text-xs text-gray-500 border-t border-gray-100">
            Not eligible (fewer than {data?.minBookings} bookings): {notEligible.map((c) => `${c.name.split(" ")[0]} (${c.bookings})`).join(", ")}
          </p>
        )}
      </div>

      {winners.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Past winners</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {winners.map((w) => (
              <button key={w.month} onClick={() => setMonth(w.month)} className="text-left rounded-lg border border-gray-100 p-3 hover:border-amber-300">
                <div className="text-xs text-gray-500">{monthLabel(w.month)}</div>
                <div className="font-semibold text-gray-900 flex items-center gap-1">
                  <MdEmojiEvents className="text-amber-500" /> {w.name}
                </div>
                <div className="text-xs text-gray-500">
                  {w.score}/100 · {inr(w.prizeCoins)}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {announcing && (
        <AnnounceForm
          month={month}
          candidate={announcing}
          onClose={() => setAnnouncing(null)}
          onDone={() => {
            setAnnouncing(null);
            q.refetch();
            history.refetch();
          }}
        />
      )}
    </div>
  );
};

export default EotmTab;
